import React, { FC } from 'react'
import { styled } from '~/app/components'
import { Typography } from '@yanolja-org/ya-com-core'
import { Icon_info } from '@yanolja-org/ya-com-system-icon'
import { DateTime } from 'luxon'
import { isInPeriod } from '~/app/utils/datetime'
import { CancellationFeePlan } from './types'
import {
  CANCELLATION_TEXT_TYPE,
  getCancellationTextType,
} from '~/app/components/order-converter/converter/LeisureOrderConverter'

interface PenaltyProps {
  title: string
  endDate?: string
  hasRefundInfo: boolean
  onClickCancelPenalty: React.MouseEventHandler<HTMLSpanElement>
}

interface ILeisureCancelPolicyText {
  hasRefundInfo: boolean
  cancellationFeePlan: CancellationFeePlan
  onClickRefundablePolicy: React.MouseEventHandler<HTMLSpanElement>
}

const Text = styled.span`
  ${Typography('body2').bold};
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
`

const PenaltyText = styled(Text)<{ hasRefundInfo: boolean }>`
  color: ${({ theme }) => theme.color.yaorange};
  flex-direction: column;
  gap: 0.2rem;
  @media (hover: hover) and (pointer: fine) {
    ${({ hasRefundInfo }) => hasRefundInfo && 'cursor: pointer;'};
  }
`

const PenaltyTextLine = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const FreeText = styled(Text)<{ hasRefundInfo: boolean }>`
  color: ${({ theme }) => theme.color.yagreen};
  @media (hover: hover) and (pointer: fine) {
    ${({ hasRefundInfo }) => hasRefundInfo && 'cursor: pointer;'};
  }
`

const NoRefundText = styled(Text)`
  color: ${({ theme }) => theme.color.yared};
`

const EndDateText = styled.span`
  margin-left: 0.2rem;
`

const IconInfo = styled(Icon_info)`
  margin-left: 0.2rem;
`

const PenaltyIcon = styled(IconInfo)`
  path {
    fill: ${({ theme }) => theme.color.yaorange};
  }
`

const FreeIcon = styled(IconInfo)`
  path {
    fill: ${({ theme }) => theme.color.yagreen};
  }
`

const Penalty: FC<PenaltyProps> = ({ title, endDate, hasRefundInfo, onClickCancelPenalty }) => {
  return (
    <PenaltyText hasRefundInfo={hasRefundInfo} onClick={onClickCancelPenalty}>
      <PenaltyTextLine>
        {title} {hasRefundInfo && <PenaltyIcon size={1.4} unit={'rem'} />}
      </PenaltyTextLine>
      {!!endDate && <PenaltyTextLine>{endDate}</PenaltyTextLine>}
    </PenaltyText>
  )
}

/**
 * 레저 취소수수료 뱃지 문구
 *
 * @see {@link https://yanoljagroup.atlassian.net/wiki/spaces/platform/pages/1346990178/2025-Q1#FRONT-%EC%B7%A8%EC%86%8C-%EB%85%B8%EC%B6%9C-%EC%A0%95%EC%B1%85}
 * 취소 노출 정책 참고
 */
const LeisureCancelPolicyText: FC<ILeisureCancelPolicyText> = ({
  hasRefundInfo,
  cancellationFeePlan: feePlan,
  onClickRefundablePolicy,
}) => {
  const typeCode = feePlan.cancellationTypeCode
  const endDate = DateTime.fromFormat(feePlan.endDateTime, 'yyyy-MM-dd HH:mm')
    .setLocale('ko-KR')
    .toFormat('(yyyy.MM.dd (ccc) HH:mm 전까지)')

  const isNowInPeriod = isInPeriod({
    startDateTime: feePlan.startDateTime,
    endDateTime: feePlan.endDateTime,
  })

  const textType = getCancellationTextType({ typeCode, isNowInPeriod })

  return (
    <>
      {(() => {
        switch (textType) {
          case CANCELLATION_TEXT_TYPE.FREE: {
            return (
              <FreeText hasRefundInfo={hasRefundInfo} onClick={onClickRefundablePolicy}>
                무료취소
              </FreeText>
            )
          }
          case CANCELLATION_TEXT_TYPE.FREE_PERIOD: {
            return (
              <FreeText hasRefundInfo={hasRefundInfo} onClick={onClickRefundablePolicy}>
                무료취소 <EndDateText>{endDate}</EndDateText>{' '}
                {hasRefundInfo && <FreeIcon size={1.4} unit={'rem'} />}
              </FreeText>
            )
          }
          case CANCELLATION_TEXT_TYPE.FEE: {
            return (
              <Penalty
                title={'취소수수료 부과'}
                endDate={endDate}
                hasRefundInfo={hasRefundInfo}
                onClickCancelPenalty={onClickRefundablePolicy}
              />
            )
          }
          case CANCELLATION_TEXT_TYPE.FEE_RATIO: {
            return (
              <Penalty
                title={`취소수수료 ${feePlan.fee}%`}
                endDate={endDate}
                hasRefundInfo={hasRefundInfo}
                onClickCancelPenalty={onClickRefundablePolicy}
              />
            )
          }
          case CANCELLATION_TEXT_TYPE.NO_CANCELLATION_REFUND: {
            return <NoRefundText>취소 및 환불불가</NoRefundText>
          }

          case CANCELLATION_TEXT_TYPE.NO_REFUND: {
            return <NoRefundText>환불불가</NoRefundText>
          }
        }
      })()}
    </>
  )
}

export default LeisureCancelPolicyText
